/** Tabul „3D” din Garaj: modelul mașinii cu ușile, capota, volanul, pedalele, bordul și luminile legate de rig. */
import * as THREE from 'three/webgpu';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { derived } from '../data/calc';
import type { VehicleDefinition } from '../data/types';
import { createLoader, loadCar, type LoadedCar, type ModelSource } from '../cars/loader';
import type { Openable } from '../cars/rig';
import { chosenVariant, saveVariant, type Variant } from '../cars/variant';

export interface Viewer3D {
  show(v: VehicleDefinition, paintHex: string): Promise<void>;
  dispose(): void;
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

export function createViewer3D(host: HTMLElement, source: ModelSource, transcoderPath: string | null): Viewer3D {
  host.innerHTML = `<div class="v3-wrap"><div class="v3-canvas"></div><div class="v3-info"></div></div><div class="v3-ctrl"></div>`;
  const canvasHost = host.querySelector('.v3-canvas') as HTMLElement, info = host.querySelector('.v3-info') as HTMLElement, ctrl = host.querySelector('.v3-ctrl') as HTMLElement;
  const renderer = new THREE.WebGPURenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.shadowMap.enabled = true;
  canvasHost.appendChild(renderer.domElement);
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x1a2029);
  const camera = new THREE.PerspectiveCamera(42, 1, 0.02, 100);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.maxPolarAngle = Math.PI * 0.49;
  scene.add(new THREE.HemisphereLight(0xdfe8ff, 0x3a3630, 1.4));
  const sun = new THREE.DirectionalLight(0xffffff, 2.2);
  sun.position.set(4, 7, 5);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  scene.add(sun);
  const floor = new THREE.Mesh(new THREE.CircleGeometry(6, 48), new THREE.MeshStandardMaterial({ color: 0x2a3038, roughness: 0.6, metalness: 0.2 }));
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  scene.add(floor);
  let ready = false;
  let car: LoadedCar | null = null, def: VehicleDefinition | null = null;
  let variant: Variant = chosenVariant(), lod = 'lod0', interior = false, t = 0;
  const open: Record<string, number> = {}, target: Record<string, number> = {};
  const state = { steer: 0, throttle: 0, brake: 0, clutch: 0, rpm: 900, kmh: 0, low: false, high: false, brakeL: false, rev: false, indL: false, indR: false, hazard: false, spin: 0 };
  const loader = createLoader(renderer, transcoderPath ?? '');

  function resize(): void {
    const w = canvasHost.clientWidth, h = canvasHost.clientHeight;
    if (w < 2 || h < 2) return;
    renderer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  const ro = new ResizeObserver(resize);
  ro.observe(canvasHost);

  function setCamera(): void {
    if (!car || !def) return;
    if (interior) {
      const sw = car.root.getObjectByName('steering_wheel');
      const p = sw ? sw.getWorldPosition(new THREE.Vector3()) : new THREE.Vector3(0.35, 1, 0);
      camera.position.set(p.x, p.y + 0.36, p.z - 0.5);
      controls.target.set(p.x, p.y + 0.05, p.z + 2.5);
      controls.minDistance = 0.01;
      controls.maxDistance = 3;
    } else {
      const L = def.dimensions.lengthMm / 1000;
      camera.position.set(L * 0.72, 1.25, L * 0.78);
      controls.target.set(0, 0.45, 0);
      controls.minDistance = 2;
      controls.maxDistance = 14;
    }
    camera.fov = interior ? 70 : 42;
    camera.updateProjectionMatrix();
    car.rig.setInteriorDetail(interior);
  }

  function controlsHtml(): string {
    if (!car || !def) return '';
    const parts: Array<[Openable, string]> = [['door_FL', 'Ușă FS'], ['door_FR', 'Ușă FD'], ['door_RL', 'Ușă SS'], ['door_RR', 'Ușă SD'], ['hood', 'Capotă'], ['trunk', 'Portbagaj'], ['tailgate', 'Hayon']];
    const btn = (id: string, label: string, on: boolean) => `<button data-a="${id}" aria-pressed="${on}">${label}</button>`;
    const lock = derived(def).steeringLockDeg;
    return `<div class="v3-row">${parts.filter(([p]) => car!.rig.isOpenable(p)).map(([p, l]) => btn('open:' + p, l, (target[p] ?? 0) > 0)).join('')}</div>
      <div class="v3-row">${btn('light:low', 'Faruri', state.low)}${btn('light:high', 'Fază lungă', state.high)}${btn('light:brakeL', 'Frână', state.brakeL)}${btn('light:indL', '◀ Semnal', state.indL)}${btn('light:indR', 'Semnal ▶', state.indR)}${btn('light:hazard', 'Avarie', state.hazard)}${btn('light:rev', 'Marșarier', state.rev)}</div>
      <label class="v3-sl">Volan <input type="range" min="-1" max="1" step="0.01" value="${state.steer}" data-s="steer"><output>${(car.rig.steeringWheelDeg()).toFixed(0)}° volan · ${(state.steer * lock).toFixed(1)}° roți</output></label>
      <label class="v3-sl">Accelerație <input type="range" min="0" max="1" step="0.01" value="${state.throttle}" data-s="throttle"></label>
      <label class="v3-sl">Frână <input type="range" min="0" max="1" step="0.01" value="${state.brake}" data-s="brake"></label>
      ${def.transmission.type === 'manual' ? `<label class="v3-sl">Ambreiaj <input type="range" min="0" max="1" step="0.01" value="${state.clutch}" data-s="clutch"></label>` : ''}
      <label class="v3-sl">Turație <input type="range" min="0" max="${def.engine.limiter.rpm}" step="50" value="${state.rpm}" data-s="rpm"><output>${state.rpm} rot/min</output></label>
      <label class="v3-sl">Viteză <input type="range" min="0" max="${def.performance.topSpeedKmh}" step="1" value="${state.kmh}" data-s="kmh"><output>${state.kmh} km/h</output></label>
      <div class="v3-row">${btn('cam:int', interior ? 'Vedere exterior' : 'Vedere interior', interior)}
        <span class="seg">${(['mobile', 'desktop'] as Variant[]).map((x) => `<button data-a="variant:${x}" aria-pressed="${x === variant}">${x === 'mobile' ? 'Mobil' : 'Desktop'}</button>`).join('')}</span>
        <span class="seg">${['lod0', 'lod1', 'lod2'].map((x) => `<button data-a="lod:${x}" aria-pressed="${x === lod}">${x.toUpperCase()}</button>`).join('')}</span></div>`;
  }

  function infoHtml(): string {
    if (!car) return '';
    // triunghiurile și draw call-urile părților vizibile acum (interiorul detaliat doar în camera de interior)
    let tris = 0, calls = 0;
    car.root.traverseVisible((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const g = m.geometry;
      tris += (g.index ? g.index.count : g.attributes.position.count) / 3;
      calls += Array.isArray(m.material) ? m.material.length : 1;
    });
    const ok = car.issues.length === 0;
    return `<b>${variant === 'mobile' ? 'Mobil' : 'Desktop'} · ${lod.toUpperCase()}</b> · ${((car.bytes) / 1024).toFixed(0)} KB · ${Math.round(tris).toLocaleString('ro-RO')} triunghiuri · ${calls} draw calls<br>
      ${ok ? '<span class="ok">✓ dimensiuni și pivoturi în ±5 mm față de fișă</span>' : `<span class="bad">✗ ${car.issues.length} probleme: ${car.issues.slice(0, 2).map((i) => esc(i.message)).join('; ')}</span>`}<br>
      <span class="muted">Machetă greybox generată în Blender din fișa JSON; modelul final se pune în locul ei cu aceleași nume.</span>`;
  }

  ctrl.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('button') as HTMLButtonElement | null;
    if (!b?.dataset.a || !car) return;
    const [kind, arg] = b.dataset.a.split(':');
    if (kind === 'open') target[arg] = (target[arg] ?? 0) > 0 ? 0 : 1;
    else if (kind === 'light') (state as Record<string, unknown>)[arg] = !(state as Record<string, unknown>)[arg];
    else if (kind === 'cam') { interior = !interior; setCamera(); }
    else if (kind === 'variant') { variant = arg as Variant; saveVariant(variant); void reload(); return; }
    else if (kind === 'lod') { lod = arg; void reload(); return; }
    ctrl.innerHTML = controlsHtml();
  });
  ctrl.addEventListener('input', (e) => {
    const s = e.target as HTMLInputElement;
    if (!s.dataset.s) return;
    (state as unknown as Record<string, number>)[s.dataset.s] = Number(s.value);
    const out = s.parentElement?.querySelector('output');
    if (out && car && def) {
      if (s.dataset.s === 'steer') out.textContent = `${(state.steer * derived(def).steeringLockDeg * def.steering.rackRatio).toFixed(0)}° volan · ${(state.steer * derived(def).steeringLockDeg).toFixed(1)}° roți`;
      if (s.dataset.s === 'rpm') out.textContent = `${state.rpm} rot/min`;
      if (s.dataset.s === 'kmh') out.textContent = `${state.kmh} km/h`;
    }
  });

  async function reload(paint?: string): Promise<void> {
    if (!def) return;
    const keepPaint = paint ?? lastPaint;
    if (car) { car.dispose(); car = null; }
    info.innerHTML = '<span class="muted">Se încarcă modelul…</span>';
    try {
      car = await loadCar(def, variant, lod, source, loader);
      variant = car.variant;
      car.root.traverse((o) => { if ((o as THREE.Mesh).isMesh) { o.castShadow = true; o.receiveShadow = true; } });
      car.rig.setPaint(keepPaint);
      scene.add(car.root);
      for (const k of Object.keys(open)) open[k] = 0;
      for (const k of Object.keys(target)) target[k] = 0;
      setCamera();
      ctrl.innerHTML = controlsHtml();
    } catch (e) {
      info.innerHTML = `<span class="bad">Modelul nu s-a încărcat: ${esc(String(e))}</span>`;
    }
  }
  let lastPaint = '#c0c4c8';

  let lastT = performance.now(), infoT = 0;
  renderer.setAnimationLoop((now: number) => {
    if (!ready) return;
    const dt = Math.min(0.05, (now - lastT) / 1000);
    lastT = now;
    t += dt;
    controls.update();
    if (car && def) {
      for (const k of Object.keys(target)) {
        open[k] = (open[k] ?? 0) + Math.sign(target[k] - (open[k] ?? 0)) * Math.min(Math.abs(target[k] - (open[k] ?? 0)), dt * 1.2);
        car.rig.setOpen(k as Openable, open[k]);
      }
      const lock = (derived(def).steeringLockDeg * Math.PI) / 180;
      car.rig.setSteering(state.steer * lock);
      const r = 0.31;
      state.spin += (state.kmh / 3.6 / r) * dt;
      car.rig.setWheelSpin([state.spin, state.spin, state.spin, state.spin]);
      car.rig.setPedals({ throttle: state.throttle, brake: state.brake, clutch: state.clutch });
      car.rig.setGauges({ rpm: state.rpm, speedKmh: state.kmh, tempC: 90, fuel: 0.7 });
      const blink = Math.floor(t * 2.6) % 2 === 0;
      car.rig.setLights({ drl: true, low: state.low, high: state.high, tail: state.low, brake: state.brakeL || state.brake > 0.05, reverse: state.rev, indicatorL: (state.indL || state.hazard) && blink, indicatorR: (state.indR || state.hazard) && blink });
      if (now - infoT > 500) { infoT = now; info.innerHTML = infoHtml(); }
    }
    renderer.render(scene, camera);
  });

  return {
    async show(v, paintHex) {
      if (!ready) { await renderer.init(); ready = true; resize(); }
      lastPaint = paintHex;
      if (def?.id === v.id && car) { car.rig.setPaint(paintHex); return; }
      def = v;
      await reload(paintHex);
    },
    dispose() {
      renderer.setAnimationLoop(null);
      ro.disconnect();
      if (car) car.dispose();
      renderer.dispose();
      host.innerHTML = '';
    }
  };
}
